import DTMFAudio from '@/libs/dtmfAudio'
import autoAlert from '@/assets/sounds/BEEP.wav'
import _get from 'lodash/get'
import newVonixSipHandler from '@/libs/VonixSipHandler'
import { CONNECTING, RINGING, CONNECTED, HOLD } from '@/constants/callStatus'
import { VOICE_CHANNEL } from '@/constants/channels'
import { DIRECTION_IN, DIRECTION_OUT, DIRECTION_AUTO } from '@/constants/directions'
import { ESTABLISHING, INITIAL, TERMINATING } from '@/constants/sessionStatuses'
import { SessionState } from 'sip.js'
import { isVonixPhoneFocused, mainWindowToForeground } from '@/libs/helpers'
import { ROUTE_SETTINGS } from '@/router/constants'
import events from '@/libs/events'
import { Mp3Recorder } from '@/libs/Mp3Recorder'
import { isSystemPause, isSystemPauseId, resolvePauseReasonId, hasAttendancesWithLock, canClearSchedulePause, shouldFallbackRepause } from '@/libs/systemPause'
import { createSystemPauseDial } from '@/libs/systemPauseDial'
import { createSystemPauseRepause } from '@/libs/systemPauseRepause'
import { isMicAvailable, watchAudioDevices } from '@/libs/audioDevices'
import notifyMicMissing from '@/libs/notifyMicMissing'
import { notifyError } from '@/libs/notify'

let vonixSipHandler
let sessionList = {}
let sessionStart = {}
let ipcRegistered = false
let pausePromise = null
let latestExtension
let forceSipDisconnect = false
let timeoutReconnect = null
let systemPauseDial = null
let systemPauseRepause = null
let commandsInFlight = 0
let lastAgentStatus = null
let fallbackRepauseFired = false
let requestedPauseReasonId = null

const router = document.router
const busEvents = events()
const root = { root: true }
const SIP_CONNECTED = 'SIP_CONNECTED'
const SIP_RETRY_SECS = 'SIP_RETRY_SECS'
const SIP_EXTENSIONS = 'SIP_EXTENSIONS'
const SET_CREDENTIALS = 'SET_CREDENTIALS'
const actionUpdateAttendanceVoice = 'attendanceModule/updateAttendanceVoice'
const ipcRenderer = window.ipcRenderer
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const getters = {
  sessionList: () => sessionList,
  retrySecs: (state) => state.sipRetrySecs
}

const isDialLocked = () => Boolean(systemPauseDial?.isLocked())

const hasAttendances = () => hasAttendancesWithLock({
  sessionCount: Object.keys(sessionList).length + Object.keys(sessionStart).length,
  dialLocked: isDialLocked()
})

const getSystemPauseDial = (ctx) => {
  if (systemPauseDial) return systemPauseDial
  systemPauseDial = createSystemPauseDial({
    unpause: () => ctx.dispatch('unpause'),
    call: (params) => ctx.dispatch('call', { $fromSystemPause: true, params }),
    setSchedulePause: (reasonId) => ctx.dispatch('agentModule/setSchedulePause', reasonId, root),
    clearSchedulePause: () => ctx.dispatch('agentModule/clearSchedulePause', null, root),
    notify: notifyError
  })
  return systemPauseDial
}

// Ramal ocupado para a central: comando *1x ainda no ar ou sessão de voz.
const getSystemPauseRepause = (ctx) => {
  if (systemPauseRepause) return systemPauseRepause
  systemPauseRepause = createSystemPauseRepause({
    sendPause: (reasonId) => {
      ctx.dispatch('agentModule/loadingSession', null, root)
      requestedPauseReasonId = reasonId
      callCommand(ctx, `*11*${reasonId}`)
    },
    isBusy: () => commandsInFlight > 0 || hasAttendances(),
    notify: notifyError
  })
  return systemPauseRepause
}

// Toda chamada SIP abre o microfone com deviceId exact: sem ele a ação falha em silêncio
const ensureMic = (ctx) => {
  if (isMicAvailable(ctx.rootState.settingsModule)) return true
  notifyMicMissing()
  return false
}

const callCommand = (ctx, command) => {
  if (!ensureMic(ctx)) return Promise.resolve()

  return new Promise((resolve) => {
    commandsInFlight++
    vonixSipHandler
      .call(command, ctx.rootState.settingsModule.inputPhone)
      .events
      .onEstablishing(() => DTMFAudio.playCustom('dial'))
      .onEstablished(() => DTMFAudio.stop())
      .onTerminated(() => DTMFAudio.stop())
      .onTerminated(() => { commandsInFlight = Math.max(0, commandsInFlight - 1) })
      .onTerminated(resolve)
  })
}

const sipDisconnect = async () => {
  if (!vonixSipHandler) return
  forceSipDisconnect = true
  await vonixSipHandler.disconnect()
  await wait(250)
  forceSipDisconnect = false
}

const actions = {
  async disconnect () {
    await sipDisconnect()
  },

  async connect (ctx) {
    if (timeoutReconnect) clearTimeout(timeoutReconnect)
    timeoutReconnect = null

    const provision = ctx.rootGetters['agentModule/provision']
    if (!provision) return sipDisconnect()

    latestExtension = provision.extension
    ctx.commit(SET_CREDENTIALS, provision)
    watchAudioDevices()
    await sipDisconnect()

    vonixSipHandler = newVonixSipHandler()
    ctx.commit('SIP_LOADING')

    let retryConnection = 7
    const sipEvents = getSipEvents(ctx)

    const startSIP = () => (
      vonixSipHandler.connect(provision, sipEvents).catch(async (err) => {
        console.log({ err })
        --retryConnection
        if (retryConnection <= 0) return ctx.commit('SET_ERROR', err)

        let timeSecs = 9

        while (timeSecs > 0) {
          ctx.commit(SIP_RETRY_SECS, timeSecs--)
          await wait(1000)
        }

        return startSIP()
      })
    )

    startSIP().then((agent) => {
      if (!agent) return router.push({ name: ROUTE_SETTINGS })
      sipEvents.onMessage({ Event: 'Agent', agent })
    })
  },

  accept (ctx, attendance) {
    const { dispatch, rootGetters } = ctx
    const attendanceId = attendance?.id ?? attendance
    const session = sessionList[attendanceId]
    const input = rootGetters['settingsModule/input']
    const canEnd = [null, undefined, '', 'Terminated'].includes(session?.state)
    if (canEnd) return dispatch('ended', attendanceId)
    if (!ensureMic(ctx)) return

    try {
      vonixSipHandler.accept(session, input)
      let updateAttendance = { attendanceId: session.id, status: CONNECTED, answerAt: Date.now() }
      if (attendance.direction) updateAttendance = { ...updateAttendance, direction: attendance.direction }
      dispatch(actionUpdateAttendanceVoice, updateAttendance, root)
      dispatch('holdCalls', session)
    } catch {
      dispatch('ended', attendanceId)
    }
  },

  cancel (_, attendanceId) {
    const session = sessionList[attendanceId]
    if (!session) return
    if (session.state === TERMINATING) return
    const cancelSession = session.state === ESTABLISHING || session.state === INITIAL
    if (cancelSession) vonixSipHandler.cancel(session)
  },

  call (ctx, params) {
    const { rootGetters, dispatch } = ctx
    if (!rootGetters['connectionModule/connected']) return
    if (!ensureMic(ctx)) return

    if (params?.$fromSystemPause) {
      params = params.params
    } else {
      const isAdmin = ctx.rootState.settingsModule.provision?.profile === 'ADMIN'
      const agent = rootGetters['agentModule/getAgent']
      if (!isAdmin && (isSystemPause(agent) || isDialLocked())) return getSystemPauseDial(ctx).request(params)
    }

    let contact; let queueId; let shouldAnonymize = false
    Array.isArray(params) ? [contact, queueId, shouldAnonymize] = params : contact = params

    dispatch('holdCalls')
    const micDevice = rootGetters['settingsModule/input']
    const session = vonixSipHandler.call(contact, micDevice, queueId)
    sessionStart[session.id] = session

    const onFinish = () => {
      delete sessionStart[session.id]
      if (!sessionList[session.id]) return applyPendingPauseOnEarlyExit(ctx)
      dispatch('ended', session.id)
      DTMFAudio.playHangup()
    }

    session.$status = CONNECTING

    const onProgress = () => {
      session.$status = RINGING
      if (!sessionList[session.id]) return
      dispatch(actionUpdateAttendanceVoice, { attendanceId: session.id, status: RINGING }, root)
    }

    const onAccept = () => {
      session.$status = CONNECTED
      if (!sessionList[session.id]) return
      dispatch(actionUpdateAttendanceVoice, { attendanceId: session.id, status: CONNECTED }, root)
      dispatch('verifyAndSetHold', session.id)
    }

    session.events
      .onProgress(onProgress)
      .onAccept(onAccept)
      .onTerminating(onFinish)
      .onTerminated(onFinish)

    setTimeout(() => {
      if (sessionStart[session.id]) {
        delete sessionStart[session.id]
        sessionList[session.id] = session
        dispatch('attendanceModule/addAttendanceVoice', {
          queue: queueId,
          shouldAnonymize,
          contact,
          id: session.id,
          status: session.$status,
          direction: DIRECTION_OUT,
          channel: VOICE_CHANNEL
        }, root)
      }
    }, 1000)
  },

  verifyAndSetHold (ctx, attendanceId) {
    const hasEstablishedSession = Object.values(sessionList).some((session) => {
      if (session.id === attendanceId) return false
      if (session.state !== SessionState.Established) return false
      return !ctx.rootState.attendanceModule.current[session.id].onHold
    })

    if (hasEstablishedSession) ctx.dispatch('hold', attendanceId)
  },

  hangup (_, attendanceId) {
    const session = sessionList[attendanceId]
    if (!session) return
    if (session.state === TERMINATING) return

    const cancelSession = Boolean(session.state === ESTABLISHING || session.state === INITIAL)

    cancelSession
      ? vonixSipHandler.cancel(session)
      : vonixSipHandler.hangup(session)
  },

  hold (ctx, attendanceId) {
    const session = sessionList[attendanceId]
    if (session.state !== SessionState.Established) return
    vonixSipHandler.hold(session, true)
    ctx.dispatch(actionUpdateAttendanceVoice, { attendanceId, onHold: true, status: HOLD }, root)
  },

  holdCalls (ctx, session) {
    for (const sessionId in sessionList) {
      const canHold = !session || sessionId !== session?.id
      if (canHold) ctx.dispatch('hold', sessionId)
    }
  },

  unhold (ctx, attendanceId) {
    const session = sessionList[attendanceId]
    const conference = ctx.rootState.attendanceModule.conference

    vonixSipHandler.hold(session, false)

    ctx.dispatch(actionUpdateAttendanceVoice, { attendanceId: session.id, onHold: false, status: CONNECTED }, root)

    if (!conference.length) ctx.dispatch('holdCalls', session)
  },

  mute (ctx, attendanceId) {
    const session = sessionList[attendanceId]

    vonixSipHandler.handleMute(true, session)

    ctx.dispatch(actionUpdateAttendanceVoice, { attendanceId: session.id, isMuted: true }, root)
  },

  unmute (ctx, attendanceId) {
    const session = sessionList[attendanceId]

    vonixSipHandler.handleMute(false, session)

    ctx.dispatch(actionUpdateAttendanceVoice, { attendanceId: session.id, isMuted: false }, root)
  },

  DTMFAudioPlay (_, digit) {
    DTMFAudio.play(digit)
  },

  sendDTMF (_, data) {
    const session = sessionList[data.attendanceId]
    vonixSipHandler.dtmf(session, data.tone)
  },

  ended (ctx, sessionId) {
    if (!sessionList[sessionId]) return
    delete sessionList[sessionId]

    ipcRenderer.send('set-attendances', Object.keys(sessionList).length)
    ctx.dispatch('attendanceModule/discardAttendanceVoice', sessionId, root)
  },

  transfer ({ dispatch }, { sessionId, destination, destinationId }) {
    const actualSession = sessionList[sessionId]

    if (destinationId) {
      const destinationSession = sessionList[destinationId]
      vonixSipHandler.transfer(actualSession, destinationSession)

      dispatch('ended', sessionId)
      dispatch('ended', destinationId)
      return
    }

    vonixSipHandler.transfer(actualSession, destination)
    dispatch('ended', sessionId)
  },

  transferEntryNumber ({ dispatch }, { sessionId, entryNumber }) {
    let destination = entryNumber.number
    if (entryNumber.destinationType === 'AGENTCODE') destination = `*20*${destination}`
    dispatch('transfer', { sessionId, destination })
  },

  createConference ({ dispatch }, { attendanceId, sessions }) {
    const mainSession = sessionList[attendanceId]
    const sessionsConference = []

    sessionsConference.push(mainSession)
    sessionsConference.push(...Object.keys(sessionList).filter(key => sessions.includes(key)).map(key => sessionList[key]))
    vonixSipHandler.conference(sessionsConference, true)

    sessionsConference.map((session) => dispatch('attendanceModule/addAttendanceConference', session.id, root))

    for (const session of sessionsConference) {
      if (session.id !== mainSession.id) {
        dispatch('unhold', session.id)
      }
    }
  },

  login (ctx, agentId) {
    const { commit, rootState, dispatch } = ctx
    if (!ensureMic(ctx)) return
    const session = vonixSipHandler.call(`*10*${agentId}`, rootState.settingsModule.inputPhone)
    const attendanceId = session.id
    sessionList[attendanceId] = session

    session.events
      .onEstablishing(() => DTMFAudio.playVonixRingtone())
      .onEstablishing(() => commit('SET_LOGIN', { attendanceId, loading: true, showKeyboard: false }))

      .onEstablished(() => DTMFAudio.stop())
      .onEstablished(() => commit('SET_LOGIN', { attendanceId, loading: false, showKeyboard: true }))

      .onTerminated(() => DTMFAudio.stop())
      .onTerminated(() => commit('SET_LOGIN', { attendanceId: null, loading: false, showKeyboard: false }))
      .onTerminated(() => delete sessionList[attendanceId])

    dispatch('attendanceModule/clearHistory', null, { root: true })
    dispatch('calendarModule/clearHistory', null, { root: true })
  },

  playBeepAlert (_, volume) {
    playAutoAlert(volume)
  },

  async logout (ctx) {
    const { dispatch } = ctx
    if (hasAttendances()) return dispatch('agentModule/setScheduleLogout', null, root)
    if (!ensureMic(ctx)) return

    dispatch('agentModule/loadingSession', null, root)
    dispatch('callCommand', '*10')
    await wait(1000)

    sessionList = {}
    sessionStart = {}
    dispatch('calendarModule/clearHistory', null, { root: true })
    dispatch('attendanceModule/clearTextAttendances', null, root)
    dispatch('agentModule/clearScheduleLogout', null, root)
    dispatch('agentModule/updateAgent', { logged: false, loggedAt: 0 }, root)
  },

  destroyConference ({ dispatch }, { attendanceId }) {
    const sessionsConference = []
    sessionsConference.push(...Object.keys(sessionList).map(key => sessionList[key]))

    dispatch('attendanceModule/discardConference', null, root)
    vonixSipHandler.conference(sessionsConference, false)

    dispatch('holdCalls', sessionList[attendanceId])
  },

  callCommand: (ctx, command) => (
    callCommand(ctx, command)
  ),

  pause (ctx, reasonId) {
    if (hasAttendances()) return ctx.dispatch('agentModule/setSchedulePause', reasonId, root)
    if (!ensureMic(ctx)) return
    if (isSystemPauseId(reasonId)) return getSystemPauseRepause(ctx).request(reasonId)
    systemPauseRepause?.cancel()
    if (pausePromise) return

    ctx.dispatch('agentModule/loadingSession', null, root)
    requestedPauseReasonId = reasonId
    pausePromise = callCommand(ctx, `*11*${reasonId}`).then(() => {
      pausePromise = null
    })
  },

  unpause (ctx) {
    const { dispatch } = ctx
    if (!ensureMic(ctx)) return
    systemPauseRepause?.cancel()
    requestedPauseReasonId = null
    dispatch('agentModule/loadingSession', null, root)
    dispatch('callCommand', '*12')
  }
}

const mutations = {
  SIP_LOADING (state) {
    state.sipLoading = true
  },

  SET_LOGIN (state, payload) {
    state.login = Object.assign(state.login, payload)
  },

  SET_ERROR (state, error) {
    state.error = error
  },

  [SIP_CONNECTED]: (state, sipConnected) => Object.assign(state, { sipConnected, sipLoading: false }),
  [SIP_RETRY_SECS]: (state, sipRetrySecs) => Object.assign(state, { sipRetrySecs: state.sipConnected ? 0 : sipRetrySecs }),
  [SET_CREDENTIALS]: (state, provision) => Object.assign(state, { credentials: provision ? { ...provision } : {} }),
  [SIP_EXTENSIONS]: (state, extensions) => {
    state.extensions = Object.assign({}, state.extensions || {}, extensions || {})
  }
}

const getSipEvents = (ctx) => {
  if (!ipcRegistered) {
    ipcRenderer.on('listen-call-by-extension', (_, url) => {
      let destination = String(url || '').replace('sip://', 'sip:').trim()
      if (destination.includes('.vonixcc.com.br') && !destination.includes('pbx.vonixcc.com.br')) destination = destination.replace('.vonixcc.com.br', '.pbx.vonixcc.com.br')
      ctx.dispatch('call', destination)
    })
    ipcRegistered = true
  }

  const messageEvents = {
    Agent: ({ agent }) => {
      if (ctx.state.sipLoading) {
        ctx.dispatch('agentModule/setConnectSip', { loading: false }, { root: true })
        ctx.commit(SIP_CONNECTED, true)
        ipcRenderer.send('sip-connected')
      }

      const sessionState = ctx.rootState?.agentModule?.session ?? {}
      const clearSchedule = canClearSchedulePause({ paused: agent.paused, schedulePause: sessionState.schedulePause, dialLocked: isDialLocked() })
      if (clearSchedule) ctx.dispatch('agentModule/clearSchedulePause', null, root)

      const prevAgent = ctx.rootState?.agentModule?.session?.agent ?? {}
      agent.pauseReasonId = resolvePauseReasonId({
        status: agent.status,
        sipReasonId: agent.pauseReasonId,
        prevStatus: prevAgent.status,
        prevReasonId: prevAgent.pauseReasonId,
        requestedReasonId: requestedPauseReasonId
      })
      if (agent.status === 'PAUSED') requestedPauseReasonId = null

      ctx.dispatch('agentModule/updateAgent', agent, root)
      if (systemPauseDial && agent?.status) systemPauseDial.onAgentStatus(agent.status)
      if (systemPauseRepause && agent?.status) systemPauseRepause.onAgentStatus(agent.status)
    },

    Dial: (message) => {
      const queue = message.QueueId
      const callId = message.CallId
      const destination = message.Destination
      const sipHeaders = parseExtra(message.extra)

      let sessions = Object.values(sessionStart)

      if (sessions.length > 1) {
        sessions = Object.values(sessionStart).filter((session) => {
          const contact = getContact(session)
          return contact.startsWith(destination) || destination.startsWith(contact)
        })
      }

      if (!sessions.length) {
        const existingSessionInList = Object.values(sessionList).find((session) => {
          const contact = getContact(session)
          return contact.includes(destination) || destination.includes(contact)
        })

        if (existingSessionInList) {
          const optionCallback = ctx.rootState.attendanceModule.optionCallback
          const foundOptionCallback = optionCallback.callbackCallId === existingSessionInList.id
          if (foundOptionCallback) {
            ctx.dispatch('attendanceModule/updateOptionCallback', {
              callbackCallId: callId
            }, root)
          }

          ctx.dispatch('attendanceModule/updateAttendanceVoice', {
            attendanceId: existingSessionInList.id,
            callId: callId,
            queue
          }, root)
        }
        return
      }

      const session = sessions.at(0)
      delete sessionStart[session.id]

      const contact = session.$isClickToCall ? sipHeaders?.to ?? destination : getContact(session)
      const contactName = session?.request?.from?.displayName ?? sipHeaders?.name

      session._id = callId
      sessionList[session.id] = session

      ipcRenderer.send('set-attendances', Object.keys(sessionList).length)

      ctx.dispatch('attendanceModule/addAttendanceVoice', {
        id: session.id,
        channel: VOICE_CHANNEL,

        queue,
        sipHeaders,

        contact,
        contactName,

        status: session?.$status || CONNECTING,
        direction: session?.$direction || DIRECTION_OUT
      }, root)

      if (session.$isClickToCall) {
        setTimeout(() => (
          ctx.dispatch(actionUpdateAttendanceVoice, {
            attendanceId: session.id,
            status: CONNECTED,
            answerAt: Date.now()
          }, root)
        ), 200)
      }
    }
  }

  const tryReconnect = async () => {
    if (!timeoutReconnect) return
    if (timeoutReconnect) clearTimeout(timeoutReconnect)

    ctx.commit(SIP_RETRY_SECS, 0)
    ctx.commit('SIP_LOADING')
    const agentReconnected = await vonixSipHandler.reconnect().catch(() => null)
    if (agentReconnected) return null

    let timeSecs = 9

    while (timeSecs > 1) {
      if (!timeoutReconnect) return
      ctx.commit(SIP_RETRY_SECS, timeSecs--)
      await wait(1000)
    }

    if (!timeoutReconnect) return
    timeoutReconnect = setTimeout(tryReconnect, 1000)
  }

  return {
    onDisconnect: () => {
      ctx.commit(SIP_CONNECTED, false)
      ipcRenderer.send('sip-disconnected')
      if (forceSipDisconnect) return
      timeoutReconnect = setTimeout(tryReconnect, 1000)
    },

    onNotify (extensions) {
      ctx.commit(SIP_EXTENSIONS, extensions)
    },

    onInvite: (invitation) => {
      const headers = invitation.request.headers
      const data = vonixSipHandler.handleHeaders(headers)
      const sipHeaders = headers ? Object.keys(headers).reduce((acc, key) => ({ ...acc, [key]: _get(headers, `${key}[0].raw`) })) : {}
      if (data.callId) invitation._id = data.callId

      if (data.queue) {
        const queueProps = ctx.rootState.agentModule?.queuesProperties?.[data.queue]
        if (!queueProps) { ctx.dispatch('agentModule/getQueuesProperties', data.queue, { root: true }) }
      }

      const contact = getContact(invitation)
      const isClickToCall = contact === latestExtension

      if (isClickToCall) {
        if (hasAttendances()) return vonixSipHandler.cancel(invitation, { statusCode: 486 })

        invitation.$isClickToCall = true
        invitation.$status = RINGING
        invitation.$direction = DIRECTION_OUT
        sessionStart[invitation.id] = invitation
      }

      const dataDirection = String(data.direction || '').toUpperCase()
      const direction = [DIRECTION_AUTO, DIRECTION_IN].includes(dataDirection) ? dataDirection : (data.hasAutoAnswer ? DIRECTION_AUTO : DIRECTION_IN)
      const rejectAutomaticaDial = hasAttendances() && direction === DIRECTION_AUTO
      if (rejectAutomaticaDial) return vonixSipHandler.cancel(invitation, { statusCode: 486 })

      const schedulePause = ctx.rootGetters['agentModule/hasSchedulePause']
      const scheduleLogout = ctx.rootGetters['agentModule/hasScheduleLogout']
      const journeyPrePause = ctx.rootGetters['agentModule/hasJourneyPrePause']

      const rejectCall = (rejectReason) => {
        delete sessionStart[invitation.id]
        vonixSipHandler.cancel(invitation, { statusCode: 486 })
        if (['agentOffilne', 'hasAutoRejection', 'hasJourneyPrePause'].includes(rejectReason)) return
        if (schedulePause) return ctx.dispatch('pause', schedulePause)
        if (scheduleLogout) return ctx.dispatch('logout')
      }

      const onTerminated = () => {
        delete sessionStart[invitation.id]
        if (sessionList[invitation.id] !== invitation) return
        ctx.dispatch('ended', invitation.id)
        busEvents.emit('cancel-attendance-offer', { id: invitation.id })
        ipcRenderer.send('cancel-attendance-offer', { id: invitation.id })
        DTMFAudio.playHangup()
      }

      const onEstablished = () => {
        if (!sessionList[invitation.id]) return
        ctx.dispatch(actionUpdateAttendanceVoice, { attendanceId: invitation.id, status: CONNECTED }, root)
        DTMFAudio.stop()
      }

      invitation.events
        .onEstablished(onEstablished)
        .onTerminated(onTerminated)

      const agent = ctx.rootGetters['agentModule/getAgent']
      const agentPaused = Boolean(agent?.paused)
      const agentOffilne = Boolean(!agent?.logged)

      if (agentPaused) return rejectCall('agentPaused')
      if (agentOffilne) return rejectCall('agentOffilne')
      if (schedulePause) return rejectCall('hasSchedulePause')
      if (scheduleLogout) return rejectCall('hasScheduleLogout')
      if (journeyPrePause) return rejectCall('hasJourneyPrePause')

      const hasAutoRejection = ctx.rootGetters['agentModule/hasAutoRejection']
      if (hasAutoRejection) {
        const voiceRejectionType = ctx.rootGetters['settingsModule/hasCallsRejection']
        const rejectClickToCall = isClickToCall && voiceRejectionType === 'AUTO'
        const dndMode = (voiceRejectionType === dataDirection || voiceRejectionType === 'ALL')
        if (dndMode || rejectClickToCall) return rejectCall('hasAutoRejection')
      }

      if (invitation.$isClickToCall) {
        if (!ensureMic(ctx)) return rejectCall('micMissing')
        return vonixSipHandler.accept(invitation, ctx.rootGetters['settingsModule/input'])
      }

      sessionList[invitation.id] = invitation

      const attendance = {
        id: invitation.id,
        contact,
        direction,
        sipHeaders,
        contactName: invitation.request.from.displayName,

        queue: data.queue,
        status: RINGING,
        channel: VOICE_CHANNEL,
        callId: data.callId,
        autoAnswer: data.hasAutoAnswer
      }

      ipcRenderer.send('set-attendances', Object.keys(sessionList).length)
      ctx.dispatch('attendanceModule/addAttendanceVoice', attendance, root)

      if (attendance.autoAnswer) {
        const playAutoPlayback = ctx.rootState.settingsModule.autoPlayback === 'yes'
        if (playAutoPlayback) {
          const volume = ctx.rootGetters['settingsModule/autoAlertVolume']
          playAutoAlert(volume)
        }
        const windowToForeground = ctx.rootGetters['settingsModule/windowToForeground']
        if (windowToForeground === 'yes') mainWindowToForeground()
        return ctx.dispatch('accept', attendance)
      }

      DTMFAudio.playVonixRingtone()

      const queueProps = ctx.rootGetters['agentModule/queueProperties'](data.queue)
      const noQueueProps = Object.keys(queueProps).length === 0
      if (noQueueProps || queueProps?.anonymizePhone === 'yes') attendance.anonymizePhone = true

      isVonixPhoneFocused().then((hasFocusInVP) => {
        hasFocusInVP
          ? busEvents.emit('open-attendance-offer', attendance)
          : ipcRenderer.send('open-attendance-offer', attendance)
      })
    },

    onMessage: (message) => {
      const messageEvent = messageEvents[message?.Event]
      if (messageEvent) messageEvent(message)
    }
  }
}

const getContact = (session) => {
  const contact = String(session?.outgoingRequestMessage?.toURI?.normal?.user ?? '').trim()
  return contact || String(session?.request?.from?.uri?.raw?.user || '').trim()
}

// Saída antecipada do onFinish de call(): a sessão nunca chegou a sessionList (falha < 1 s sem Dial),
// então onFinishAttendance não roda. Aplica a pausa agendada aqui se não houver outro atendimento.
const applyPendingPauseOnEarlyExit = (ctx) => {
  const isAdmin = ctx.rootState.settingsModule.provision?.profile === 'ADMIN'
  if (isAdmin || hasAttendances()) return
  const schedulePause = ctx.rootGetters['agentModule/hasSchedulePause']
  const optionCallbackShow = ctx.rootState.attendanceModule?.optionCallback?.show
  const hasSchedule = schedulePause !== null && schedulePause !== undefined && schedulePause !== false
  if (hasSchedule && !optionCallbackShow) ctx.dispatch('pause', schedulePause)
}

// Fallback da volta para a 25: a central publicou DIALING/RINGING/ONTHEPHONE -> ONLINE sem sessão de voz.
const fallbackRepause = (ctx, payload) => {
  const prevStatus = lastAgentStatus
  const status = payload?.status
  lastAgentStatus = status
  if (status !== 'ONLINE') fallbackRepauseFired = false

  const schedulePause = ctx.state.agentModule?.session?.schedulePause
  const fire = shouldFallbackRepause({
    prevStatus,
    status,
    schedulePause,
    hasVoiceSession: hasAttendances(),
    optionCallbackShow: Boolean(ctx.state.attendanceModule?.optionCallback?.show),
    alreadyFired: fallbackRepauseFired
  })
  if (!fire) return
  fallbackRepauseFired = true
  console.warn('[systemPause] fallback: volta para a pausa 25 pelo status do agente')
  ctx.dispatch('sipModule/pause', schedulePause, root)
}

const middlewares = {
  'agentModule/UPDATE_AGENT': (ctx, { payload }) => {
    fallbackRepause(ctx, payload)

    const attendances = ctx.getters['attendanceModule/getAttendances']
    const isNotReachable = !payload?.reachable

    if (isNotReachable) {
      Object.keys(attendances).map((attendanceId) => (
        ctx.dispatch('sipModule/hangup', attendanceId, root)
      ))
    }
  }
}

export default {
  namespaced: true,
  getters,
  middlewares,
  mutations,
  actions,
  state: {
    extensions: {},
    credentials: {},
    sipRetrySecs: 0,
    sipLoading: false,
    sipConnected: false,

    login: {
      loading: false,
      error: null,
      success: false,
      showKeyboard: false,
      attendanceId: null
    },

    messaging: {
      loading: false
    },

    error: null
  }
}

const parseExtra = (extra) => {
  if (!extra) return {}

  try {
    const strJson = hex2str(extra)
    if (!strJson) return {}
    return JSON.parse(strJson)
  } catch {
    return {}
  }
}

const hex2str = (hex) => {
  let str = ''

  for (let i = 0; i < hex.length; i += 2) {
    str += String.fromCharCode(parseInt(hex.substr(i, 2), 16))
  }

  return str
}

const getMicStream = async () => {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    })
  } catch (error) {
    console.error('Erro ao acessar microfone:', error)
    return null
  }
}

export const getRecorder = async (sessionId) => {
  const session = sessionList[sessionId]
  if (!session) return { recorder: null, micStream: null }

  try {
    const micStream = await getMicStream()
    if (!micStream) {
      console.error('Não foi possível obter stream do microfone')
      return { recorder: null, micStream: null }
    }

    const ctx = new AudioContext()
    const stereoMixer = ctx.createChannelMerger(2)

    const micSource = ctx.createMediaStreamSource(micStream)
    micSource.connect(stereoMixer, 0, 0)

    session.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track && receiver.track.kind === 'audio') {
        const remoteSource = ctx.createMediaStreamSource(new MediaStream([receiver.track]))
        remoteSource.connect(stereoMixer, 0, 1)
      }
    })

    const mp3RecorderInstance = new Mp3Recorder()
    await mp3RecorderInstance.configure(stereoMixer, 2, 128)

    return { recorder: mp3RecorderInstance, micStream }
  } catch (error) {
    console.error('Erro ao configurar gravação:', error)
    return { recorder: null, micStream: null }
  }
}

const playAutoAlert = (volume = 100) => {
  const audio = new Audio(autoAlert)
  audio.volume = Math.max(0.3, Math.min(1.0, volume / 100))

  audio.play()
}
